import { create } from 'zustand';

type ThemeMode = 'light' | 'dark';
type Language = 'en' | 'sq' | 'mk' | 'de';

interface AppState {
  theme: ThemeMode;
  language: Language;
  sidebarCollapsed: boolean;
  sidebarVisible: boolean;
  breadcrumbs: Array<{ label: string; path?: string }>;
  fullscreen: boolean;
  notificationsPanelOpen: boolean;

  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  setLanguage: (language: Language) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarVisible: (visible: boolean) => void;
  setBreadcrumbs: (breadcrumbs: Array<{ label: string; path?: string }>) => void;
  addBreadcrumb: (item: { label: string; path?: string }) => void;
  toggleFullscreen: () => void;
  toggleNotificationsPanel: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  theme: (localStorage.getItem('theme') as ThemeMode) || 'light',
  language: (localStorage.getItem('language') as Language) || 'en',
  sidebarCollapsed: localStorage.getItem('sidebarCollapsed') === 'true',
  sidebarVisible: true,
  breadcrumbs: [],
  fullscreen: false,
  notificationsPanelOpen: false,

  setTheme: (theme) => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
    set({ theme });
  },

  toggleTheme: () => {
    set((state) => {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
      return { theme: newTheme };
    });
  },

  setLanguage: (language) => {
    localStorage.setItem('language', language);
    set({ language });
  },

  toggleSidebar: () => {
    set((state) => {
      const newCollapsed = !state.sidebarCollapsed;
      localStorage.setItem('sidebarCollapsed', String(newCollapsed));
      return { sidebarCollapsed: newCollapsed };
    });
  },

  setSidebarCollapsed: (collapsed) => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
    set({ sidebarCollapsed: collapsed });
  },

  setSidebarVisible: (visible) => set({ sidebarVisible: visible }),

  setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),

  addBreadcrumb: (item) =>
    set((state) => ({
      breadcrumbs: [...state.breadcrumbs, item],
    })),

  toggleFullscreen: () =>
    set((state) => ({ fullscreen: !state.fullscreen })),

  toggleNotificationsPanel: () =>
    set((state) => ({ notificationsPanelOpen: !state.notificationsPanelOpen })),
}));
